import type { TabsProps } from "antd";
import { Layout, Row, Space, Tabs, Typography } from "antd";

import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import EditIcon from "../../../assets/icons/edit.svg";
import SearchIcon from "../../../assets/icons/search-white.svg";
import Flame from "../../../assets/images/flame.webp";
import PopCorn from "../../../assets/images/popcorn.webp";
import FooterComponent from "../../../components/FooterComponent";
import MessageTabLabelComponent from "../../../components/MessageTabLabelComponent";
import MessageTabListComponent from "../../../components/MessageTabListComponent";
import SearchComponent from "../../../components/Search/SearchComponent";
import { CONVERSATIONS_FILTER_TYPES, CONVERSATION_TYPES, C_CURRENT_TAB, FOOTER_IDS, F_CURRENT_TAB } from "../../../helpers/constant";
import { usePreviousLocationContext } from "../../../helpers/PreviousLocationContext";
import { useAppSelector } from "../../../redux/hooks";
import {
  getUserConversations,
  resetCurrentFilter,
  resetSearchConversations,
  searchConversation,
  setSearchValue,
  getUnreadByTypes,
  updateUnreadTypes,
  resetType,
} from "../../../redux/messages/messagesSlice";
import { selectFilteredConversations, selectUnreadTypes } from "../../../redux/messages/Selectors/messageSeletors";
import { AppDispatch, StoreState } from "../../../redux/store";

import { socket } from "../../../socket";
import "./index.less";

const FanMessagePage = () => {
  const previousLocation = usePreviousLocationContext();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const { searchValue, isSearching } = useSelector((state: StoreState) => state.messages);
  const unreadTypes = useAppSelector(selectUnreadTypes);
  const dispatch = useDispatch<AppDispatch>();
  const shouldFetch = useRef(true);
  const fetchedActiveKeys = useRef(new Set<string>());
  const shouldChangeTab: boolean = !previousLocation?.pathname.startsWith("/fan/requestMedia");
  const [activeKey, setActiveKey] = useState(window.localStorage.getItem(C_CURRENT_TAB) || CONVERSATION_TYPES.SUBSCRIPTION);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const conversationsData = useAppSelector((state) => selectFilteredConversations(state, activeKey, CONVERSATIONS_FILTER_TYPES.ALL));

  // useEffect(() => {
  //   if (shouldFetch.current) {
  //     shouldFetch.current = false;
  //     dispatch(getUnreadByTypes());
  //   }

  //   socket.on("unread-messages", handleUnreadMessages);

  //   return () => {
  //     socket.off("unread-messages", handleUnreadMessages);
  //   };
  // }, []);

  useEffect(() => {
    if (!fetchedActiveKeys.current.has(activeKey) && activeKey !== CONVERSATION_TYPES.SEARCH && conversationsData.length === 0) {
      fetchedActiveKeys.current.add(activeKey);
      dispatch(getUserConversations({ page: 1, type: activeKey }));
    }
  }, [activeKey, conversationsData, dispatch]);

  // Handle tab switching based on unread types changes
  // useEffect(() => {
  //   if (shouldChangeTab && unreadTypes && Object.keys(unreadTypes).length > 0) {
  //     if (unreadTypes.GENERAL && !unreadTypes.SUBSCRIPTION) {
  //       setActiveKey(CONVERSATION_TYPES.GENERAL);
  //       window.localStorage.setItem(F_CURRENT_TAB, CONVERSATION_TYPES.GENERAL);
  //     } else {
  //       setActiveKey(CONVERSATION_TYPES.SUBSCRIPTION);
  //       window.localStorage.setItem(F_CURRENT_TAB, CONVERSATION_TYPES.SUBSCRIPTION);
  //     }
  //   }
  // }, [unreadTypes, shouldChangeTab]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchValue.length >= 3) {
        dispatch(resetSearchConversations());
        dispatch(resetCurrentFilter());

        dispatch(
          searchConversation({
            search: searchValue,
            page: 1,
            type: activeKey,
          })
        );
      }
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [searchValue]);

  function handleUnreadMessages(data: any) {
    if (data && Array.isArray(data)) {
      const unreadTypesObject: { [key: string]: boolean } = {};
      data.forEach((object: { type: string; hasNewMessages: boolean }) => {
        unreadTypesObject[object.type] = object.hasNewMessages;
      });
      dispatch(updateUnreadTypes(unreadTypesObject));
    }
  };

  const onChangeTab = (activeKey: string) => {
    setActiveKey(activeKey);
    dispatch(resetCurrentFilter());
    dispatch(setSearchValue(""));
    window.localStorage.setItem(F_CURRENT_TAB, activeKey);
  };

  const tabs: TabsProps["items"] = [
    {
      key: CONVERSATION_TYPES.GENERAL,
      label: (
        <MessageTabLabelComponent
          icon={PopCorn}
          label={t("general")}
          isNew={unreadTypes?.[CONVERSATION_TYPES.GENERAL]}
          isActive={activeKey === CONVERSATION_TYPES.GENERAL}
        />
      ),
      children: <MessageTabListComponent activeKey={CONVERSATION_TYPES.GENERAL} />,
    },
    {
      key: CONVERSATION_TYPES.SUBSCRIPTION,
      label: (
        <MessageTabLabelComponent
          icon={Flame}
          label={t("your-subscriptions")}
          isNew={unreadTypes?.[CONVERSATION_TYPES.SUBSCRIPTION]}
          isActive={activeKey === CONVERSATION_TYPES.SUBSCRIPTION}
        />
      ),
      children: <MessageTabListComponent activeKey={CONVERSATION_TYPES.SUBSCRIPTION} />,
    },
  ];

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const trimmedSearchTerm = e.currentTarget.value.trim();

    dispatch(setSearchValue(trimmedSearchTerm));
    if (trimmedSearchTerm === "") {
      dispatch(resetType());
    }
  };

  const toggleSearchVisibility = () => {
    setIsSearchVisible((prev) => !prev);
  };

  useEffect(() => {
    const { state } = location;
    if (state && state.activeKey) {
      setActiveKey(state.activeKey);
      window.localStorage.setItem(F_CURRENT_TAB, state.activeKey);
    }
    let fanTab = window.localStorage.getItem(F_CURRENT_TAB);
    if (fanTab) {
      setActiveKey(fanTab);
    }
  }, [location]);

  return (
    <Layout>
      <Layout.Content className="content messageContentWrapper gradient-background-black-blue">
        <Row className="justify-content-between header-title-container-95 mb-6">
          <Typography.Text className="header-title-black-35">{t("messaging")}</Typography.Text>
          <Space className="gap-13">
            <img src={EditIcon} width={26} alt="edit" className="cursor-pointer" onClick={() => navigate("/common/messages/new")} />
            <img src={SearchIcon} width={24} alt="edit" className="cursor-pointer" onClick={() => toggleSearchVisibility()} />
          </Space>
        </Row>
        {isSearchVisible && (
          <Row className="mb-20">
            <SearchComponent placeholder={t("search-2")} value={searchValue} handleChange={handleSearch} />
          </Row>
        )}
        {isSearching ? (
          <MessageTabListComponent activeKey={activeKey} />
        ) : (
          <Tabs className="chatTabs" centered activeKey={activeKey} items={tabs} destroyInactiveTabPane={true} onChange={onChangeTab} />
        )}
      </Layout.Content>
      <FooterComponent type="fan" page={FOOTER_IDS.MESSAGING} />
    </Layout>
  );
};

export default FanMessagePage;
